import type { AppApiClient } from '../auth/AppApiClient';
import type { ResolvedGoalExplosionLoadout } from '../game/content/GoalExplosionLoadoutContracts';
import type { CatalogItem, ClaimCatalogItem, EquipmentLoadout, InventorySnapshot, UpdateEquipmentLoadout } from './InventoryContracts';

export interface InventoryPaymentOrder {
  readonly id: string;
  readonly productType: string;
  readonly productReference: string;
  readonly state: string;
  readonly checkoutUrl: string | null;
}

const categoryProducts: ReadonlyMap<string, string> = new Map([['goal-explosion', 'goal-explosion']]);
export const inventoryProductType = (category: string): string | undefined => categoryProducts.get(category);

export class InventoryApiClient {
  constructor(private readonly api: Pick<AppApiClient, 'request'>) {}

  async snapshot(signal?: AbortSignal): Promise<InventorySnapshot> {
    return (await this.api.request<{ inventory: InventorySnapshot }>('/api/inventory', { signal })).inventory;
  }

  async claim(input: ClaimCatalogItem, signal?: AbortSignal): Promise<InventorySnapshot> {
    return (await this.api.request<{ inventory: InventorySnapshot }>('/api/inventory/claims', {
      method: 'POST', body: JSON.stringify(input), signal,
    })).inventory;
  }

  async equip(input: UpdateEquipmentLoadout, signal?: AbortSignal): Promise<EquipmentLoadout> {
    return (await this.api.request<{ loadout: EquipmentLoadout }>('/api/inventory/loadout', {
      method: 'PUT', body: JSON.stringify(input), signal,
    })).loadout;
  }

  async runtime(signal?: AbortSignal): Promise<ResolvedGoalExplosionLoadout> {
    return (await this.api.request<{ loadout: ResolvedGoalExplosionLoadout }>('/api/inventory/goal-explosion-runtime', { signal })).loadout;
  }

  async orders(signal?: AbortSignal): Promise<readonly InventoryPaymentOrder[]> {
    const { orders } = await this.api.request<{ orders: InventoryPaymentOrder[] }>('/api/payments/orders', { signal });
    const products = new Set(categoryProducts.values());
    return orders.filter((order) => products.has(order.productType));
  }

  async order(id: string, signal?: AbortSignal): Promise<InventoryPaymentOrder> {
    return (await this.api.request<{ order: InventoryPaymentOrder }>(`/api/payments/orders/${encodeURIComponent(id)}`, { signal })).order;
  }

  async purchase(item: CatalogItem, idempotencyKey: string, signal?: AbortSignal): Promise<InventoryPaymentOrder> {
    const productType = inventoryProductType(item.category);
    if (!productType) throw new Error('This catalog category has no payment product.');
    return (await this.api.request<{ order: InventoryPaymentOrder }>('/api/payments/orders', {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, signal,
      body: JSON.stringify({ productType, productReference: item.id, amountCents: item.priceCents }),
    })).order;
  }
}

/** Return destinations are selected from the authorized server order, never a URL product hint. */
export async function routePaymentReturn(
  client: Pick<InventoryApiClient, 'order'>,
  orderId: string,
  destinations: Readonly<Record<string, (id: string) => void | Promise<void>>>,
  isCurrent: () => boolean,
  signal?: AbortSignal,
): Promise<boolean> {
  const order = await client.order(orderId, signal);
  if (!isCurrent() || signal?.aborted) return false;
  const destination = Object.hasOwn(destinations, order.productType) ? destinations[order.productType] : undefined;
  if (!destination) throw new Error('This payment product has no return destination.');
  await destination(order.id);
  return true;
}

export function inventoryCheckoutUrl(value: string): string {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com' || url.username || url.password || url.port) {
    throw new Error('Unexpected inventory checkout URL.');
  }
  return url.href;
}
